// Advanced Calculator Comprehensive Tutorial Guide
// Detailed, feature-specific tutorial for the Advanced Retirement Calculator
// Last Updated: October 2026
// Note: Tutorial now opens as a draggable sidebar - can be moved around the screen while using the calculator
// Covers Monte Carlo, Historical Analysis, Dynamic Strategies, bridge buckets, Division 296, and all advanced features

import React, { useState, useEffect } from 'react';
import { FinancialConfig } from './FinancialConfig.js';

const fmtAud = (n) => `$${Number(n).toLocaleString('en-AU')}`;

export const AdvancedTutorialGuide = () => {
    const [activeStep, setActiveStep] = useState(0);
    const tutorialRef = React.useRef(null);

    // Auto-scroll to top of tutorial when step changes
    useEffect(() => {
        if (tutorialRef.current) {
            tutorialRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [activeStep]);

    const tutorialSteps = [
        {
            title: "Welcome to the Advanced Retirement Simulator",
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700">
                        The Advanced Retirement Calculator is a sophisticated planning tool that uses two powerful analysis methods:
                        <strong> Monte Carlo simulation</strong> (1,000 random scenarios) and <strong>Historical Backtesting</strong> (98 years of actual market data from 1928-2025).
                    </p>
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">Key Capabilities:</h4>
                        <ul className="list-disc list-inside text-purple-700 space-y-1">
                            <li><strong>Monte Carlo Simulation:</strong> Runs 1,000 different market scenarios to calculate success probability</li>
                            <li><strong>Historical Backtesting:</strong> Test your plan using actual market returns from any specific historical period (1928-2025)</li>
                            <li><strong>4 Withdrawal Strategies:</strong> Fixed, Dynamic SWR, Vanguard Guardrails, Floor & Ceiling</li>
                            <li><strong>Phased Retirement:</strong> Model partners retiring at different ages with ongoing employment income</li>
                            <li><strong>Bridge / Delayed Super Access:</strong> Use cash and outside-super assets before drawing super in the Fixed strategy</li>
                            <li><strong>Mortgage Payoff Strategy Comparison:</strong> Compare keeping repayments, paying off debt from super or outside cash, and partial payoff options</li>
                            <li><strong>Lump Sum Withdrawals:</strong> Add one-off spending events such as cars, renovations, holidays, or gifts</li>
                            <li><strong>Lump Sum Contributions:</strong> Add one-off super payments such as inheritances, downsizer contributions, or catch-up contributions with cap flags</li>
                            <li><strong>Custom Portfolio Allocation:</strong> Set exact percentages for 5 asset classes</li>
                            <li><strong>What-If Scenarios:</strong> Test changes in retirement period, fees, and sequence of returns risk</li>
                            <li><strong>Scenario Management:</strong> Save, load, and compare multiple retirement strategies</li>
                        </ul>
                    </div>
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Who Should Use This Calculator:</h4>
                        <ul className="list-disc list-inside text-blue-700 space-y-1">
                            <li>Those wanting rigorous statistical analysis of retirement readiness</li>
                            <li>Planners needing to test multiple scenarios</li>
                            <li>Anyone with substantial super ($500k+) requiring sophisticated planning</li>
                            <li>DIY investors comfortable with asset allocation decisions</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Monte Carlo vs Historical Analysis",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Understanding the Two Analysis Methods:</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🎲 Monte Carlo Simulation</h5>
                        <p className="text-sm text-blue-700 mb-2">Generates 1,000 random future scenarios based on:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Your portfolio allocation</strong> - Asset mix determines risk/return characteristics</li>
                            <li><strong>Historical volatility</strong> - How much each asset class fluctuates</li>
                            <li><strong>Random variation</strong> - Markets don't follow a straight line</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Output:</strong> Success rate (e.g., "82% chance of success" means 820 out of 1,000 scenarios succeeded), median outcomes, percentile ranges
                        </div>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Best for:</strong> Understanding probability and risk tolerance. "What are the chances my plan will work?"
                        </div>
                    </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📊 Historical Backtesting</h5>
                        <p className="text-sm text-green-700 mb-2">Test your plan using actual market data from any specific historical period (1928-2025):</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Real market returns</strong> - Actual S&P 500, Australian shares, international shares, bonds, and cash returns</li>
                            <li><strong>Real inflation data</strong> - Includes high inflation periods (1970s-80s)</li>
                            <li><strong>Sequence of returns risk</strong> - Test specific challenging periods (e.g., 2000, 2007)</li>
                            </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>How to use:</strong> Select a start year (e.g., 1980) to model retiring in that specific year. Use the historical backtest / rolling period views to compare your plan across many actual historical starts.
                        </div>
                        <div className="mt-2 p-2 bg-green-100 text-xs text-green-800 rounded">
                            <strong>Best for:</strong> Testing against worst-case scenarios. "How would my plan have done during the GFC, dot-com crash, 1970s stagflation?"
                        </div>
                        </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">🎯 Which to Use?</h5>
                        <div className="text-sm text-yellow-800 space-y-2">
                            <div><strong>Use BOTH!</strong> They complement each other:</div>
                            <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                                <li><strong>Monte Carlo</strong> shows probability and risk ranges</li>
                                <li><strong>Historical</strong> shows actual worst-case scenarios</li>
                                <li>If both show 80%+ success → High confidence</li>
                                <li>If historical fails but Monte Carlo succeeds → Plan may not survive real crises</li>
                                <li>If both fail → Need to adjust plan (save more, spend less, retire later)</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Personal & Partner Details",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Setting Up Your Profile:</h4>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">👤 Your Age & Gender</h5>
                        <p className="text-sm text-gray-700 mb-2">Critical for life expectancy calculations:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li><strong>Age:</strong> Used with your selected period length to set the projection horizon</li>
                            <li><strong>Gender:</strong> Affects life expectancy statistics (females typically live 3-4 years longer)</li>
                            <li>Used for Age Pension eligibility and Medicare Levy Surcharge calculations</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💰 Your Super Balance</h5>
                        <p className="text-sm text-gray-700 mb-2">Total superannuation today - the foundation of your retirement income:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Enter accumulation + pension phase balances combined</li>
                            <li>Check your super fund statement or myGov for exact figure</li>
                            <li>Calculator assumes you're retired or retiring immediately</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📈 Transfer Balance Cap (TBC) Used</h5>
                        <p className="text-sm text-gray-700 mb-2">Advanced tax optimization feature:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li><strong>Transfer Balance Cap:</strong> Maximum you can transfer to tax-free pension phase. The calculator uses the current rates file, including scheduled indexation.</li>
                            <li>If you've already started a pension, enter amount used</li>
                            <li>Remaining cap = current personal cap - TBC Used</li>
                            <li>Excess stays in accumulation (15% earnings tax vs 0% in pension)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Example:</strong> If the current cap is ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M and Super = $2.5M, then ${(FinancialConfig.TRANSFER_BALANCE_CAP / 1e6).toFixed(1)}M can move to pension phase and the rest stays in accumulation.
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">💑 Partner Details (if couple)</h5>
                        <p className="text-sm text-purple-700 mb-2">Same inputs for your partner:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li>Age, Gender, Super Balance, TBC Used</li>
                            <li>Affects Age Pension (couples have different thresholds)</li>
                            <li>Combined assets tested against couple limits</li>
                            <li>Death benefit planning (survivor inherits remaining super)</li>
                        </ul>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🏥 Private Health Insurance Status</h5>
                        <p className="text-sm text-orange-700 mb-2">Affects Medicare Levy Surcharge (MLS):</p>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li>If no private health + income above threshold → Pay MLS (1-1.5% extra tax)</li>
                            <li>Thresholds are read from the current tax settings used by the calculator</li>
                            <li>Calculator auto-calculates MLS based on your income</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "New Inputs & What They Affect",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Current Advanced Calculator Input Map:</h4>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">Income and Pension-Test Inputs</h5>
                        <ul className="list-disc list-inside text-blue-700 text-sm space-y-1 ml-4">
                            <li><strong>Other Taxable Income:</strong> non-employment assessable income outside super. It affects tax and the Age Pension income test.</li>
                            <li><strong>Your / Partner Part-time Employment Income:</strong> wage income while retired. The calculator applies the Work Bonus allowance for eligible Age Pension recipients.</li>
                            <li><strong>Rent Paid Fortnightly:</strong> used to estimate Commonwealth Rent Assistance where the household receives Age Pension and is eligible.</li>
                            <li><strong>Other Non-taxable Income:</strong> income not counted for tax, but still included in household cashflow.</li>
                            <li><strong>Other Assets:</strong> assessable non-super assets for the Age Pension assets test. Do not double-count the super balance here.</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">Rules, Tax and Health Settings</h5>
                        <ul className="list-disc list-inside text-green-700 text-sm space-y-1 ml-4">
                            <li><strong>Include Pension?</strong> toggles Australian Age Pension estimates in projections.</li>
                            <li><strong>Pension Growth Rate:</strong> one annual real (above-CPI) indexation assumption for future Age Pension projections. It grows maximum pension rates, part-pension cut-offs, supplements, and rent assistance caps. Asset/income free areas and deeming thresholds stay CPI-flat in today&apos;s dollars; deeming percentages stay fixed. Simplifies ASIC&apos;s separate CPI and wage indexation tracks into one dial (default 0.3%).</li>
                            <li><strong>General Transfer Balance Cap:</strong> sets the cap used for retirement-phase tax treatment. The default comes from the current rates file.</li>
                            <li><strong>Your / Partner Private Health Insurance:</strong> affects Medicare Levy Surcharge estimates when income is above the relevant threshold.</li>
                        </ul>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">Scenario Timing and Target Inputs</h5>
                        <ul className="list-disc list-inside text-purple-700 text-sm space-y-1 ml-4">
                            <li><strong>Start Year (Single Scenario):</strong> chooses the first calendar year for historical backtesting.</li>
                            <li><strong>Period Length:</strong> controls how many years the plan is projected.</li>
                            <li><strong>Target Final Balance:</strong> preserves a bequest or minimum ending balance, lowering sustainable income if set above zero.</li>
                            <li><strong>Monte Carlo Target Income:</strong> the annual income tested across random market paths. If blank, the calculator can populate it from the computed sustainable income after a run.</li>
                        </ul>
                    </div>

                    <div className="bg-amber-50 p-4 rounded-lg">
                        <h5 className="font-medium text-amber-800 mb-2">Newer Planning Modules</h5>
                        <ul className="list-disc list-inside text-amber-700 text-sm space-y-1 ml-4">
                            <li><strong>Bridge / Delayed Super Access:</strong> uses cash and outside-super buckets before drawing super.</li>
                            <li><strong>Mortgage Payoff Strategy Comparison:</strong> compares keeping repayments, full payoff, later payoff, partial payoff, and payoff funding source.</li>
                            <li><strong>Lump Sum Withdrawals:</strong> schedules one-off withdrawals by age or plan year.</li>
                            <li><strong>Lump Sum Contributions:</strong> schedules one-off contributions, recipient, contribution type, and cap warnings.</li>
                            <li><strong>Alternative Assets:</strong> models property, infrastructure, collectibles, and other alternatives with income/growth assumptions.</li>
                            <li><strong>Annuity & Defined Benefits:</strong> adds guaranteed or employer pension income streams and optional indexation.</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "🔄 Phased Retirement (Couples Only)",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Model Partners Retiring at Different Ages:</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg border-2 border-purple-200">
                        <h5 className="font-medium text-purple-800 mb-2">🆕 NEW FEATURE: Phased Retirement</h5>
                        <p className="text-sm text-purple-700 mb-2">
                            For couples where partners retire at different times, this feature provides realistic modeling of:
                        </p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Individual retirement ages:</strong> One partner working while the other is retired</li>
                            <li><strong>Ongoing salary & SG contributions:</strong> Working partner continues building super</li>
                            <li><strong>Separate super tracking:</strong> Each partner's balance tracked independently</li>
                            <li><strong>Super drawdown trigger (Fixed MSI):</strong> Start withdrawals when either partner retires, or only when the primary retires — only shown when Phased Retirement is on</li>
                            <li><strong>Dynamic Age Pension:</strong> Transitions from single → couple rates</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">✅ When to Use Phased Retirement</h5>
                        <div className="text-sm text-blue-700 space-y-2">
                            <p><strong>Use phased retirement when:</strong></p>
                            <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                                <li>Partners have different retirement ages (5+ year gap typical)</li>
                                <li>One partner plans to work longer</li>
                                <li>Significant age gap between partners</li>
                                <li>Want to model ongoing salary + SG contributions</li>
                                <li>Need accurate per-partner super projections</li>
                            </ul>
                            <p className="mt-2"><strong>Use standard mode when:</strong></p>
                            <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                                <li>Both retiring at same time</li>
                                <li>Both already retired</li>
                                <li>Age difference is minimal (&lt;2 years)</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🔧 How to Enable</h5>
                        <div className="text-sm text-green-700 space-y-2">
                            <p><strong>Step 1:</strong> Set Relationship Status to "Couple"</p>
                            <p><strong>Step 2:</strong> Toggle ON "🔄 Enable Phased Retirement"</p>
                            <p><strong>Step 3:</strong> Enter retirement details for each partner:</p>
                            <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                                <li><strong>Retirement Age:</strong> When each partner plans to retire</li>
                                <li><strong>Salary (while working):</strong> Current salary (set to $0 if already retired)</li>
                                <li><strong>SG Contributions:</strong> Automatically calculated at 12%</li>
                            </ul>
                            <p className="mt-2"><strong>Step 4:</strong> Choose the <strong>Super drawdown trigger (Fixed MSI)</strong> that appears inside this box:</p>
                            <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                                <li><strong>When either partner retires:</strong> start sustainable super withdrawals as soon as the first partner reaches their retirement age (only that partner’s accessible super is drawn; the still-working partner is not forced into SIS minimums).</li>
                                <li><strong>Only when you (primary) retire:</strong> wait until the primary person reaches their retirement age before Fixed MSI super withdrawals begin.</li>
                            </ul>
                        </div>
                        <div className="mt-2 p-2 bg-amber-100 text-xs text-amber-950 rounded border border-amber-300">
                            <strong>Important:</strong> The drawdown trigger is part of Phased Retirement. If Phased Retirement is unchecked, the control is hidden and the either/primary choice is <em>inoperative</em> — the plan uses standard household Fixed MSI (no either/primary gate).
                        </div>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Example:</strong> Partner A age 65 (retired), Partner B age 55 (retiring at 67, salary $120K) → System models 12 years of Partner B working while Partner A is retired
                        </div>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">📊 What Changes with Phased Retirement?</h5>
                        <div className="text-sm text-yellow-700 space-y-2">
                            <p><strong>1. Separate Super Balance Tracking:</strong></p>
                            <ul className="list-disc list-inside text-yellow-600 text-sm ml-4">
                                <li>Each partner&apos;s balance tracked independently</li>
                                <li>Withdrawals only from partners who have reached their retirement age</li>
                                <li>Working partner&apos;s super continues growing without forced minimums</li>
                            </ul>
                            <p className="mt-2"><strong>2. Employment Income Included:</strong></p>
                            <ul className="list-disc list-inside text-yellow-600 text-sm ml-4">
                                <li>Working partner's salary included in household income</li>
                                <li>SG (12%) automatically added to super each year</li>
                                <li>Reduces need for super withdrawals during working phase</li>
                            </ul>
                            <p className="mt-2"><strong>3. Dynamic Age Pension:</strong></p>
                            <ul className="list-disc list-inside text-yellow-600 text-sm ml-4">
                                <li>Phase 1: Single rate for retired partner (if eligible)</li>
                                <li>Phase 2: Single rate continues while one works</li>
                                <li>Phase 3: Couple rate when both retired & 67+</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">🎯 Key Benefits</h5>
                        <ul className="list-disc list-inside text-orange-600 text-sm space-y-1 ml-4">
                            <li><strong>Realistic Planning:</strong> Models actual retirement timing</li>
                            <li><strong>Higher Sustainable Income:</strong> Working partner's salary boosts household income</li>
                            <li><strong>Individual Tracking:</strong> See each partner's super balance & pension eligibility</li>
                            <li><strong>Better Decisions:</strong> Test impact of retiring earlier/later</li>
                        </ul>
                        <div className="mt-2 p-2 bg-orange-200 text-xs text-orange-900 rounded">
                            <strong>Pro Tip:</strong> Your super balance may grow in early years if one partner is still working and contributing SG while market returns exceed withdrawals. This is correct and realistic!
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">✅ All Strategies Supported</h5>
                        <p className="text-sm text-red-700 mb-2">
                            Phased retirement works with ALL withdrawal strategies:
                        </p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li>✅ Fixed Sustainable Income</li>
                            <li>✅ Dynamic SWR</li>
                            <li>✅ Vanguard Guardrails</li>
                            <li>✅ Floor & Ceiling</li>
                        </ul>
                        <p className="text-xs text-red-600 mt-2">
                            All strategies correctly handle per-partner balances, employment income, SG contributions, and dynamic Age Pension calculations.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Income, Assets & Pension Settings",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Other Income and Assets:</h4>
                    
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💼 Other Taxable Income (Annual)</h5>
                        <p className="text-sm text-gray-700 mb-2">Income outside super:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Part-time work, rental income, investment income, business income</li>
                            <li>Taxed at marginal rates + Medicare Levy</li>
                            <li>Reduces Age Pension eligibility (income test)</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💸 Other Non-Taxable Income</h5>
                        <p className="text-sm text-gray-700 mb-2">Tax-free income sources (rare):</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Note: Super pension income is NOT entered here (it's calculated from your super balance)</li>
                            <li>Could include: certain compensation payments, veteran's affairs payments</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">🏦 Other Assessable Assets</h5>
                        <p className="text-sm text-gray-700 mb-2">Assets counted in Age Pension assets test:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Investment properties, shares outside super, cash/term deposits</li>
                            <li>Valuable personal items (boats, caravans, collections)</li>
                            <li><strong>Exclude:</strong> Home, car, household contents under $10k</li>
                        </ul>
                    </div>

                        <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🏠 Homeownership & Age Pension</h5>
                        <p className="text-sm text-blue-700 mb-2">Critical setting for Age Pension calculations:</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="font-semibold text-blue-900">Homeowner (Single)</div>
                                <div className="text-blue-800">Assets: $327k - $727k</div>
                            </div>
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="font-semibold text-blue-900">Non-Homeowner (Single)</div>
                                <div className="text-blue-800">Assets: $590k - $990k</div>
                            </div>
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="font-semibold text-blue-900">Homeowner (Couple)</div>
                                <div className="text-blue-800">Assets: $490.5k - $1,093.5k</div>
                            </div>
                            <div className="bg-blue-100 p-2 rounded">
                                <div className="font-semibold text-blue-900">Non-Homeowner (Couple)</div>
                                <div className="text-blue-800">Assets: $753.5k - $1,356.5k</div>
                            </div>
                        </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📅 Start Year & Period Length</h5>
                        <p className="text-sm text-green-700 mb-2">Simulation parameters:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Start Year:</strong> Retirement year (affects historical backtesting)</li>
                            <li><strong>Period Length:</strong> How many years to project (typically 30-40 years)</li>
                            <li>Set the horizon explicitly; many examples use 30-40 years</li>
                            </ul>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 Target Final Balance</h5>
                        <p className="text-sm text-purple-700 mb-2">Bequest/legacy goal:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>$0:</strong> Spend it all (die with zero)</li>
                            <li><strong>$500k+:</strong> Leave inheritance for children</li>
                            <li>Higher target = need to withdraw less = lower retirement income</li>
                            </ul>
                        </div>
                </div>
            )
        },
        {
            title: "SMSF Data Transfer & Multi-Asset Portfolios",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Importing Complex Portfolios from SMSF Calculator:</h4>
                    
                    <div className="bg-indigo-50 p-4 rounded-lg">
                        <h5 className="font-medium text-indigo-800 mb-2">🔄 Transfer from SMSF Calculator</h5>
                        <p className="text-sm text-indigo-700 mb-2">You can enter alternative assets directly in Advanced, or transfer a detailed portfolio from the SMSF Calculator's Asset Valuation Manager:</p>
                        <ul className="list-disc list-inside text-indigo-600 text-sm space-y-1 ml-4">
                            <li><strong>Direct entry:</strong> add residential property, commercial property, collectibles, other alternatives, and growth/yield assumptions in Advanced.</li>
                            <li><strong>Complete Portfolio Transfer:</strong> All assets transfer with individual details preserved</li>
                            <li><strong>Multiple Properties:</strong> Each residential/commercial property maintains its own value, rental income, and growth rate</li>
                            <li><strong>Alternative Assets:</strong> Infrastructure funds and other alternatives transfer with individual return rates</li>
                            <li><strong>Collectibles:</strong> Art, antiques, and other collectibles transfer with individual valuations</li>
                            <li><strong>Automatic Allocation:</strong> Portfolio percentages are automatically calculated</li>
                        </ul>
                        <div className="mt-2 p-2 bg-indigo-200 text-xs text-indigo-900 rounded">
                            <strong>How to Transfer:</strong> In SMSF Calculator → Asset Valuation Manager → Scroll to bottom → Click "Transfer to Advanced Calculator"
                        </div>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🏘️ Multiple Residential Properties</h5>
                        <p className="text-sm text-purple-700 mb-2">When transferred from SMSF, each property is displayed separately:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Individual Cards:</strong> Each property shown in its own card with name (e.g., "123 Main St Investment Property")</li>
                            <li><strong>Separate Metrics:</strong> Value, rental income, rental yield, and capital growth for each property</li>
                            <li><strong>Editable:</strong> You can adjust any property's metrics after transfer</li>
                            <li><strong>Tax Treatment:</strong> Rental income taxed at 15% (accumulation) or 0% (pension phase, age 60+)</li>
                        </ul>
                        <div className="mt-2 p-2 bg-purple-200 text-xs text-purple-900 rounded">
                            <strong>Example:</strong> Property 1: $800K @ 4.5% yield | Property 2: $650K @ 5.2% yield → Both tracked separately in simulation
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🏢 Multiple Commercial Properties</h5>
                        <p className="text-sm text-green-700 mb-2">Commercial properties work the same way as residential:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li>Each property maintains individual valuation and rental income</li>
                            <li>Typically higher rental yields than residential (5-7%)</li>
                            <li>Same tax treatment as residential rental income</li>
                            <li>Can have LRBA (Limited Recourse Borrowing Arrangement) details</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">⚡ Multiple Alternative Assets</h5>
                        <p className="text-sm text-blue-700 mb-2">Infrastructure funds and other productive alternatives:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Productive Assets:</strong> Count toward portfolio allocation (e.g., 73% infrastructure)</li>
                            <li><strong>Individual Returns:</strong> Each asset has its own expected return rate</li>
                            <li><strong>Examples:</strong> Infrastructure funds, unlisted property funds, private equity</li>
                            <li><strong>Display:</strong> Shown in green-highlighted cards with ⚡ icon</li>
                        </ul>
                    </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">🎨 Multiple Collectibles</h5>
                        <p className="text-sm text-yellow-700 mb-2">Art, antiques, and other collectibles:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li><strong>Non-Productive:</strong> Don't count toward portfolio allocation</li>
                            <li><strong>Capital Growth Only:</strong> No income, only appreciation</li>
                            <li><strong>Tracked Separately:</strong> Shown in results as "Non-Productive Assets"</li>
                            <li><strong>Total Wealth:</strong> Combined with productive assets for overall wealth calculation</li>
                        </ul>
                        <div className="mt-2 p-2 bg-yellow-200 text-xs text-yellow-900 rounded">
                            <strong>Note:</strong> Collectibles are valuable for wealth tracking but don't generate retirement income
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">📊 Productive vs Non-Productive Assets</h5>
                        <p className="text-sm text-orange-700 mb-2">Understanding the distinction:</p>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-xs">
                            <div className="bg-orange-100 p-2 rounded">
                                <div className="font-semibold text-orange-900">Productive Assets</div>
                                <div className="text-orange-800">• Shares, bonds, cash</div>
                                <div className="text-orange-800">• Infrastructure funds</div>
                                <div className="text-orange-800">• Rental properties (income)</div>
                                <div className="text-orange-800">→ Generate retirement income</div>
                            </div>
                            <div className="bg-orange-100 p-2 rounded">
                                <div className="font-semibold text-orange-900">Non-Productive Assets</div>
                                <div className="text-orange-800">• Collectibles (art, antiques)</div>
                                <div className="text-orange-800">• Personal use property</div>
                                <div className="text-orange-800">• No income stream</div>
                                <div className="text-orange-800">→ Wealth tracking only</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">✅ Benefits of Multi-Asset Transfer</h5>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li><strong>Accuracy:</strong> Each asset's unique characteristics preserved</li>
                            <li><strong>Flexibility:</strong> Adjust individual asset metrics after transfer</li>
                            <li><strong>Comprehensive:</strong> See total wealth including non-productive assets</li>
                            <li><strong>Realistic:</strong> Different rental yields and growth rates per property</li>
                            <li><strong>Time-Saving:</strong> No need to re-enter complex portfolio data</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Superannuation Fees (Advanced)",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Comprehensive Fee Modeling:</h4>
                    
                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ Fee Impact Over 30 Years</h5>
                        <div className="bg-red-100 p-3 rounded mt-2">
                            <div className="text-sm text-red-900 font-semibold mb-2">Example: $800,000 super, 30 year retirement, 7% returns:</div>
                            <div className="grid grid-cols-3 gap-2 text-xs">
                                <div className="bg-white p-2 rounded">
                                    <div className="font-semibold text-red-900">0.5% total fees</div>
                                    <div className="text-lg font-bold text-red-700">$1.76M</div>
                                    <div className="text-red-600">total spent</div>
                                </div>
                                <div className="bg-white p-2 rounded">
                                    <div className="font-semibold text-red-900">1.0% total fees</div>
                                    <div className="text-lg font-bold text-red-700">$1.61M</div>
                                    <div className="text-red-600">total spent</div>
                                </div>
                                <div className="bg-white p-2 rounded">
                                    <div className="font-semibold text-red-900">1.5% total fees</div>
                                    <div className="text-lg font-bold text-red-700">$1.46M</div>
                                    <div className="text-red-600">total spent</div>
                                </div>
                            </div>
                            <div className="text-xs text-red-800 mt-2 font-semibold">Difference between lowest and highest fees: $300,000!</div>
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💳 Administration Fees ($ flat)</h5>
                        <p className="text-sm text-gray-700 mb-2">Annual account-keeping fee:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Low-cost super: $50-$100/year</li>
                            <li>Typical super: $100-$200/year</li>
                            <li>Premium super: $200-$400/year</li>
                            <li>SMSF: $1,500-$3,000/year (enter per person if shared)</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📈 Admin Fee Percentage (% of balance)</h5>
                        <p className="text-sm text-gray-700 mb-2">Some funds charge percentage-based admin fees:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Typical: 0.05% - 0.15%</li>
                            <li>Charged in addition to flat admin fee</li>
                            <li>Check PDS (Product Disclosure Statement) for exact rate</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">📊 Investment Fees (% of balance)</h5>
                        <p className="text-sm text-gray-700 mb-2">The BIG one - ongoing management costs:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Low-cost index: 0.10% - 0.30%</li>
                            <li>Industry fund balanced: 0.50% - 0.85%</li>
                            <li>Retail fund: 0.80% - 1.50%</li>
                            <li>Active management: 1.00% - 2.00%+</li>
                        </ul>
                        <div className="mt-2 p-2 bg-orange-100 text-xs text-orange-800 rounded">
                            <strong>Reality check:</strong> Check your super fund's annual statement. Look for "Investment Fee" or "ICR" (Indirect Cost Ratio)
                        </div>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💵 Investment Fee Cap ($)</h5>
                        <p className="text-sm text-gray-700 mb-2">Some funds cap investment fees:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Example: 0.85% capped at $5,000/year</li>
                            <li>Helps large balance holders (&gt; $500k)</li>
                            <li>Leave at $999,999,999 if no cap applies</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">💱 Transaction Costs (% of balance)</h5>
                        <p className="text-sm text-gray-700 mb-2">Buy/sell spread costs:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Typical: 0.05% - 0.20%</li>
                            <li>Higher for international shares or small caps</li>
                            <li>Often hidden but disclosed in PDS</li>
                        </ul>
                    </div>

                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h5 className="font-medium text-gray-800 mb-2">🏥 Insurance Premiums ($ per year)</h5>
                        <p className="text-sm text-gray-700 mb-2">Life/TPD/Income Protection in super:</p>
                        <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
                            <li>Typical: $300 - $1,500/year depending on age and cover</li>
                            <li><strong>Retiree tip:</strong> Consider cancelling to preserve super</li>
                            <li>Check if you still need cover after retirement</li>
                        </ul>
                    </div>
                </div>
            )
        },
        {
            title: "Portfolio Allocation & Returns",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Asset Allocation - The Most Important Decision:</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 Why Asset Allocation Matters</h5>
                        <p className="text-sm text-purple-700 mb-2">Studies show ~90% of portfolio returns come from asset allocation, not security selection or timing.</p>
                        <div className="bg-purple-100 p-3 rounded mt-2 text-xs">
                            <div className="font-semibold text-purple-900 mb-1">Risk-Return Trade-off:</div>
                            <div className="text-purple-800">• More shares = Higher returns + Higher volatility</div>
                            <div className="text-purple-800">• More bonds/cash = Lower returns + Lower volatility</div>
                            <div className="text-purple-800">• Balance depends on your risk tolerance and time horizon</div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h5 className="font-medium text-blue-800 mb-2">📈 Australian Shares (%)</h5>
                            <p className="text-sm text-blue-700 mb-2">ASX listed companies:</p>
                            <ul className="list-disc list-inside text-blue-600 text-xs space-y-1 ml-4">
                                <li><strong>Historical return:</strong> ~9-10% p.a.</li>
                                <li><strong>Volatility:</strong> High (±15-20% annually)</li>
                                <li><strong>Franking credits:</strong> Tax advantage for Aussies</li>
                                <li><strong>Risk:</strong> Concentrated in banks/mining</li>
                            </ul>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <h5 className="font-medium text-green-800 mb-2">🌏 International Shares (%)</h5>
                            <p className="text-sm text-green-700 mb-2">Global stocks (US, Europe, Asia):</p>
                            <ul className="list-disc list-inside text-green-600 text-xs space-y-1 ml-4">
                                <li><strong>Historical return:</strong> ~8-10% p.a.</li>
                                <li><strong>Volatility:</strong> High (±15-20% annually)</li>
                                <li><strong>Diversification:</strong> Access to tech, healthcare giants</li>
                                <li><strong>Currency risk:</strong> AUD fluctuations matter</li>
                            </ul>
                        </div>

                        <div className="bg-orange-50 p-4 rounded-lg">
                            <h5 className="font-medium text-orange-800 mb-2">💵 US Shares/S&P 500 (%)</h5>
                            <p className="text-sm text-orange-700 mb-2">Separate US allocation:</p>
                            <ul className="list-disc list-inside text-orange-600 text-xs space-y-1 ml-4">
                                <li><strong>Historical return:</strong> ~10% p.a. (USD)</li>
                                <li><strong>Volatility:</strong> High (±15-20% annually)</li>
                                <li><strong>World's largest companies:</strong> Apple, Microsoft, etc.</li>
                            </ul>
                        </div>

                        <div className="bg-yellow-50 p-4 rounded-lg">
                            <h5 className="font-medium text-yellow-800 mb-2">📊 Bonds/Fixed Income (%)</h5>
                            <p className="text-sm text-yellow-700 mb-2">Government and corporate bonds:</p>
                            <ul className="list-disc list-inside text-yellow-600 text-xs space-y-1 ml-4">
                                <li><strong>Historical return:</strong> ~4-6% p.a.</li>
                                <li><strong>Volatility:</strong> Low (±3-5% annually)</li>
                                <li><strong>Purpose:</strong> Stability, income, diversification</li>
                                <li><strong>When to hold:</strong> Retirees, risk-averse investors</li>
                            </ul>
                        </div>

                        <div className="bg-gray-50 p-4 rounded-lg">
                            <h5 className="font-medium text-gray-800 mb-2">💰 Cash (%)</h5>
                            <p className="text-sm text-gray-700 mb-2">Bank accounts, term deposits:</p>
                            <ul className="list-disc list-inside text-gray-600 text-xs space-y-1 ml-4">
                                <li><strong>Historical return:</strong> ~2-4% p.a.</li>
                                <li><strong>Volatility:</strong> None</li>
                                <li><strong>Purpose:</strong> Emergency buffer, capital preservation</li>
                                <li><strong>Reality:</strong> Often doesn't beat inflation</li>
                            </ul>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <h5 className="font-medium text-green-800 mb-2">💚 Franking Credits (%)</h5>
                            <p className="text-sm text-green-700 mb-2">Australian tax advantage:</p>
                            <ul className="list-disc list-inside text-green-600 text-xs space-y-1 ml-4">
                                <li>Percentage of Australian dividends that are franked</li>
                                <li>Typical: 70-80% for ASX shares</li>
                                <li>Adds ~0.5-1.5% to effective returns</li>
                                <li>Only applies to Australian Shares allocation</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">📊 Sample Allocations by Risk Profile:</h5>
                        <div className="grid grid-cols-3 gap-2 text-xs mt-2">
                            <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-blue-900 mb-2">Conservative</div>
                                <div className="space-y-1 text-blue-700">
                                    <div>Aus Shares: 15%</div>
                                    <div>Intl Shares: 15%</div>
                                    <div>Bonds: 40%</div>
                                    <div>Cash: 30%</div>
                                    <div className="pt-1 border-t font-semibold">~5-6% return</div>
                                </div>
                            </div>
                            <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-green-900 mb-2">Balanced</div>
                                <div className="space-y-1 text-green-700">
                                    <div>Aus Shares: 30%</div>
                                    <div>Intl Shares: 30%</div>
                                    <div>Bonds: 30%</div>
                                    <div>Cash: 10%</div>
                                    <div className="pt-1 border-t font-semibold">~7-8% return</div>
                                </div>
                            </div>
                            <div className="bg-white p-3 rounded">
                                <div className="font-semibold text-purple-900 mb-2">Growth</div>
                                <div className="space-y-1 text-purple-700">
                                    <div>Aus Shares: 40%</div>
                                    <div>Intl Shares: 40%</div>
                                    <div>Bonds: 15%</div>
                                    <div>Cash: 5%</div>
                                    <div className="pt-1 border-t font-semibold">~8-9% return</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Dynamic Withdrawal Strategies",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Smart Withdrawal Strategies That Adapt to Markets:</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 Why Dynamic Strategies?</h5>
                        <p className="text-sm text-purple-700 mb-2">Traditional "4% rule" doesn't adjust for market conditions. Dynamic strategies:</p>
                        <ul className="list-disc list-inside text-purple-600 text-sm space-y-1 ml-4">
                            <li><strong>Spend more</strong> when markets are up and balance is high</li>
                            <li><strong>Spend less</strong> when markets crash to preserve capital</li>
                            <li>Result: Higher average spending + Better survival rate</li>
                        </ul>
                    </div>

                    <div className="space-y-3">
                        <div className="bg-blue-50 p-4 rounded-lg">
                            <h5 className="font-medium text-blue-800 mb-2">1️⃣ Fixed Sustainable Income</h5>
                            <p className="text-sm text-blue-700 mb-2">Constant inflation-adjusted amount every year</p>
                            <div className="bg-blue-100 p-2 rounded text-xs mt-2">
                                <div className="font-semibold text-blue-900">How it works:</div>
                                <div className="text-blue-800">• Calculator finds highest amount you can withdraw every year</div>
                                <div className="text-blue-800">• Amount adjusts for inflation but stays constant in real terms</div>
                                <div className="font-semibold text-blue-900 mt-1">Pros:</div>
                                <div className="text-blue-800">• Predictable income - know exactly what you'll have</div>
                                <div className="text-blue-800">• Most conservative approach</div>
                                <div className="font-semibold text-blue-900 mt-1">Cons:</div>
                                <div className="text-blue-800">• May leave large inheritance (money not used)</div>
                                <div className="text-blue-800">• Doesn't adapt if markets do better than expected</div>
                            </div>
                        </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                            <h5 className="font-medium text-green-800 mb-2">2️⃣ Dynamic SWR (Safe Withdrawal Rate)</h5>
                            <p className="text-sm text-green-700 mb-2">Recalculate each year: Current Balance × SWR%</p>
                            <div className="bg-green-100 p-2 rounded text-xs mt-2">
                                <div className="font-semibold text-green-900">How it works:</div>
                                <div className="text-green-800">• Year 1: $1M × 4% = $40K withdrawal</div>
                                <div className="text-green-800">• Year 2: $1.1M × 4% = $44K withdrawal (market up)</div>
                                <div className="text-green-800">• Year 3: $900K × 4% = $36K withdrawal (market down)</div>
                                <div className="font-semibold text-green-900 mt-1">Pros:</div>
                                <div className="text-green-800">• Never run out of money (always have something left)</div>
                                <div className="text-green-800">• Automatically adjusts to market performance</div>
                                <div className="font-semibold text-green-900 mt-1">Cons:</div>
                                <div className="text-green-800">• Income varies year-to-year</div>
                                <div className="text-green-800">• May force spending cuts after market crash</div>
                            </div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                            <h5 className="font-medium text-purple-800 mb-2">3️⃣ Vanguard Guardrails</h5>
                            <p className="text-sm text-purple-700 mb-2">Set upper/lower guardrails (e.g., ±20%) and adjust when breached</p>
                            <div className="bg-purple-100 p-2 rounded text-xs mt-2">
                                <div className="font-semibold text-purple-900">How it works:</div>
                                <div className="text-purple-800">• Start at target rate (e.g., 4.5% of balance)</div>
                                <div className="text-purple-800">• If withdrawal rate hits upper guardrail → snap to upper limit</div>
                                <div className="text-purple-800">• If withdrawal rate hits lower guardrail → snap to lower limit</div>
                                <div className="font-semibold text-purple-900 mt-1">Pros:</div>
                                <div className="text-purple-800">• Stable income with occasional adjustments</div>
                                <div className="text-purple-800">• Research-backed (Vanguard, 2024)</div>
                                <div className="font-semibold text-purple-900 mt-1">Cons:</div>
                                <div className="text-purple-800">• Can still require spending cuts if markets crash</div>
                                <div className="text-purple-800">• More complex to understand</div>
                            </div>
                        </div>

                        <div className="bg-orange-50 p-4 rounded-lg">
                            <h5 className="font-medium text-orange-800 mb-2">4️⃣ Floor & Ceiling</h5>
                            <p className="text-sm text-orange-700 mb-2">Set minimum (floor) and maximum (ceiling) spending limits</p>
                            <div className="bg-orange-100 p-2 rounded text-xs mt-2">
                                <div className="font-semibold text-orange-900">How it works:</div>
                                <div className="text-orange-800">• Set floor: 4% of balance (minimum essentials)</div>
                                <div className="text-orange-800">• Set ceiling: 6% of balance (maximum lifestyle)</div>
                                <div className="text-orange-800">• Maintain last withdrawal, constrained by floor/ceiling</div>
                                <div className="font-semibold text-orange-900 mt-1">Pros:</div>
                                <div className="text-orange-800">• Protects essentials (floor) while allowing upside (ceiling)</div>
                                <div className="text-orange-800">• Enjoy market booms without overspending</div>
                                <div className="font-semibold text-orange-900 mt-1">Cons:</div>
                                <div className="text-orange-800">• May still force cuts to floor level</div>
                                <div className="text-orange-800">• Requires understanding your true minimum needs</div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg mt-4">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ Australian Minimum Withdrawal Rates</h5>
                        <p className="text-sm text-yellow-700 mb-2">All strategies must comply with Australian superannuation minimum withdrawal rates:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li>Age 65-74: 5% minimum</li>
                            <li>Age 75-79: 6% minimum</li>
                            <li>Age 80-84: 7% minimum</li>
                            <li>Age 85+: Higher rates apply</li>
                        </ul>
                        <div className="mt-2 p-2 bg-yellow-200 text-xs text-yellow-900 rounded">
                            <strong>Impact:</strong> All strategies will converge toward similar results when minimum rates exceed calculated rates. This is correct calculator behavior enforcing Australian law.
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg mt-4">
                        <h5 className="font-medium text-green-800 mb-2">✅ Which Strategy to Choose?</h5>
                        <ul className="list-disc list-inside text-green-700 text-sm space-y-1 ml-4">
                            <li><strong>Need predictable income:</strong> Fixed Sustainable Income</li>
                            <li><strong>Want flexibility with safety:</strong> Vanguard Guardrails or Floor & Ceiling</li>
                            <li><strong>Comfortable with variability:</strong> Dynamic SWR</li>
                            <li><strong>Not sure?</strong> Use Strategy Comparison feature to see all 4 side-by-side!</li>
                            <li><strong>Test different scenarios:</strong> Use What-If tools to compare period length, fees, and market conditions</li>
                        </ul>
                        </div>
                </div>
            )
        },
        {
            title: "Bridge / Delayed Super Access",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Model Retired-but-not-drawing-super-yet:</h4>

                    <div className="bg-amber-50 p-4 rounded-lg border-2 border-amber-200">
                        <h5 className="font-medium text-amber-800 mb-2">What This Feature Does</h5>
                        <p className="text-sm text-amber-700 mb-2">
                            The bridge strategy models a common early-retirement situation: you have stopped work, but want to use cash or outside-super investments before touching super.
                        </p>
                        <ul className="list-disc list-inside text-amber-700 text-sm space-y-1 ml-4">
                            <li><strong>Cash bucket first:</strong> spending is drawn from cash before other bridge assets</li>
                            <li><strong>Outside-super investments second:</strong> the investment bucket can grow at your chosen real return</li>
                            <li><strong>Super third:</strong> super is only drawn once each member reaches their selected super access age</li>
                            <li><strong>Warnings:</strong> yearly rows flag when bridge assets are depleted before super access</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">When to Use It</h5>
                        <ul className="list-disc list-inside text-blue-700 text-sm space-y-1 ml-4">
                            <li>You retire before you want to access super.</li>
                            <li>You plan to live from bank cash, term deposits, ETFs, shares, inheritance money, or sale proceeds first.</li>
                            <li>You want to stress-test whether the bridge lasts until super becomes available.</li>
                            <li>You want to separate "stop working age" from "start drawing super age".</li>
                        </ul>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">How to Set It Up</h5>
                        <ol className="list-decimal list-inside text-green-700 text-sm space-y-1 ml-4">
                            <li>Open <strong>Bridge / Delayed Super Access</strong>.</li>
                            <li>Turn on <strong>Enable Bridge Strategy</strong>.</li>
                            <li>Enter the super access age for you and, in couple mode, your partner.</li>
                            <li>Enter a <strong>Cash Bucket</strong> for immediate spending.</li>
                            <li>Enter an optional <strong>Outside-Super Investment Bucket</strong> and real return assumption.</li>
                            <li>Use <strong>Fixed Sustainable Income</strong>, then run the calculation and check the income chart plus detailed yearly rows.</li>
                        </ol>
                    </div>

                    <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">How to Read the Results</h5>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                            <li><strong>Bridge Draw:</strong> income funded from cash or outside-super investments.</li>
                            <li><strong>Bridge Balance:</strong> remaining cash plus outside-super investment bucket.</li>
                            <li><strong>Super Withdrawal:</strong> stays at $0 before access age unless another accessible source applies.</li>
                            <li><strong>Depleted before super access:</strong> the bridge ran out before super was available, so the plan needs attention.</li>
                        </ul>
                        <p className="text-xs text-yellow-700 mt-2">
                            Phase 1 applies to the Fixed Sustainable Income engine and treats bridge assets at household level. It does not yet apply detailed personal tax, capital gains tax, or per-partner ownership rules to outside-super investments.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Mortgage Payoff Strategy Comparison",
            content: (
                <div className="space-y-4">
                    <p className="text-gray-700">
                        This section helps answer whether paying off debt improves the retirement plan, or whether keeping liquidity and super invested produces a stronger outcome.
                    </p>

                    <div className="bg-slate-50 p-4 rounded-lg">
                        <h5 className="font-medium text-slate-800 mb-2">What It Compares</h5>
                        <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                            <li><strong>Keep paying:</strong> Continue normal mortgage repayments through the projection.</li>
                            <li><strong>Pay off immediately:</strong> Clear the debt in year 1 using the selected payoff source, such as super or outside cash.</li>
                            <li><strong>Pay off later:</strong> Schedule a full payoff by plan year or age.</li>
                            <li><strong>Partial payoff:</strong> Reduce the mortgage by a set amount and keep repaying the remaining balance.</li>
                            <li><strong>Contribute instead:</strong> Only when the payoff source is outside cash, compare putting that outside cash into super while keeping the mortgage.</li>
                        </ul>
                    </div>

                    <div className="bg-white p-4 rounded-lg border border-slate-200">
                        <h5 className="font-medium text-slate-800 mb-2">Fields You Will See</h5>
                        <ul className="list-disc list-inside text-slate-700 text-sm space-y-1">
                            <li><strong>Mortgage Balance, Interest Rate, Annual Repayments:</strong> define the remaining loan and annual required outflow.</li>
                            <li><strong>Main Strategy:</strong> keep paying, pay off immediately, pay off later, or partial payoff.</li>
                            <li><strong>Payoff Funding Source:</strong> use a super lump sum withdrawal or general retirement cashflow.</li>
                            <li><strong>Schedule By and Payoff Age / Year:</strong> choose whether later payoff timing is based on your age or projection year.</li>
                            <li><strong>Partial Payoff Amount:</strong> only applies when the main strategy is partial payoff.</li>
                        </ul>
                    </div>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">How the Calculator Treats It</h5>
                        <p className="text-sm text-blue-700">
                            Mortgage repayments, interest, and payoff amounts are treated as required outflows in the Fixed Sustainable Income engine. The safe income shown for mortgage scenarios is lifestyle income after those mortgage costs.
                        </p>
                    </div>

                    <div className="bg-amber-50 p-4 rounded-lg">
                        <h5 className="font-medium text-amber-800 mb-2">Current Scope</h5>
                        <p className="text-sm text-amber-700">
                            Mortgage payoff strategy comparison is currently modelled for Fixed Sustainable Income. Other withdrawal strategies can still be run, but the mortgage comparison result is based on the Fixed engine.
                        </p>
                    </div>
                </div>
            )
        },
        {
            title: "Lump Sum Withdrawals & Contributions",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Model Real-Life One-Off Events:</h4>

                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💸 Lump Sum Withdrawals</h5>
                        <p className="text-sm text-blue-700 mb-2">
                            Use this section for one-off withdrawals from super on top of normal strategy income.
                        </p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Examples:</strong> new car, caravan, home renovation, major holiday, debt payoff, or helping family</li>
                            <li><strong>Schedule by age or plan year:</strong> age 70, or year 5 of the projection</li>
                            <li><strong>MSI impact:</strong> Safe income is recalculated because these withdrawals reduce available capital</li>
                            <li><strong>Results:</strong> Lump sums appear in yearly rows, charts, exports, and transparency trace</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Tip:</strong> Put the event in the actual year it is likely to occur. A $50,000 renovation in year 3 has a different effect from the same renovation in year 20.
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">➕ Lump Sum Super Contributions</h5>
                        <p className="text-sm text-green-700 mb-2">
                            Use this section for one-off payments into super during the projection.
                        </p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Examples:</strong> inheritance, asset sale proceeds, downsizer contribution, catch-up contribution, or spouse contribution</li>
                            <li><strong>Choose recipient:</strong> you or your partner, where couple mode applies</li>
                            <li><strong>Choose contribution type:</strong> non-concessional, concessional/personal deductible, downsizer, or spouse contribution</li>
                            <li><strong>Phased retirement:</strong> employer SG from ongoing salary is still included separately while a partner is working</li>
                            <li><strong>Cap flags:</strong> the calculator flags scheduled contributions that exceed configured contribution rules</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Current row fields:</strong> recipient, contribution type, schedule by age or plan year, amount, and optional label.
                        </div>
                    </div>

                    <div className="bg-yellow-50 border-2 border-yellow-300 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">⚠️ Important Distinction</h5>
                        <ul className="list-disc list-inside text-yellow-700 text-sm space-y-1 ml-4">
                            <li><strong>Withdrawals</strong> take money out of super and usually reduce future sustainable income.</li>
                            <li><strong>Contributions</strong> add money into super and may improve outcomes, but caps and eligibility rules matter.</li>
                            <li>Both are shown in today's dollars and included in the year-by-year projection.</li>
                        </ul>
                        <p className="text-xs text-yellow-700 mt-2">
                            Contribution warnings are educational flags, not personal tax or financial advice. Check ATO rules or seek licensed advice before acting.
                        </p>
                    </div>

                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">How to Add a Row</h5>
                        <ol className="list-decimal list-inside text-purple-700 text-sm space-y-1 ml-4">
                            <li>Open the Lump Sum Withdrawals or Lump Sum Contributions section.</li>
                            <li>Enter the age or plan year.</li>
                            <li>Enter the amount and optional label.</li>
                            <li>For contributions, choose recipient and contribution type.</li>
                            <li>Run the plan again and check the yearly table, chart, and any cap flags.</li>
                        </ol>
                    </div>
                </div>
            )
        },
        {
            title: "Annuities & Defined Benefit Pensions",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Additional Income Sources:</h4>
                    
                    <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">💰 Lifetime Annuity</h5>
                        <p className="text-sm text-blue-700 mb-2">Purchase guaranteed income for life from insurance company:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Purchase Price:</strong> Lump sum paid upfront (e.g., $200,000)</li>
                            <li><strong>Annual Payment:</strong> Fixed income received each year (e.g., $12,000/year)</li>
                            <li><strong>Indexed?:</strong> Yes = payments increase with CPI, No = fixed forever</li>
                        </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Pros:</strong> Guaranteed income for life, longevity insurance<br/>
                            <strong>Cons:</strong> Lose lump sum, poor if you die early, no flexibility
                        </div>
                        <div className="mt-2 p-2 bg-blue-100 text-xs text-blue-800 rounded">
                            <strong>Calculator impact:</strong> Reduces starting super balance by purchase price, adds guaranteed income stream
                        </div>
                    </div>

                        <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">🏛️ Defined Benefit Pension</h5>
                        <p className="text-sm text-green-700 mb-2">For public servants, military, some corporate employees:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Annual Amount:</strong> Fixed pension from employer (e.g., $30,000/year)</li>
                            <li><strong>Indexed?:</strong> Yes = grows with CPI (common for public sector), No = fixed</li>
                            <li>Paid for life, often includes spouse reversion</li>
                            </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Common schemes:</strong> CSS, PSS, DFRDB, university pensions
                        </div>
                        <div className="mt-2 p-2 bg-green-100 text-xs text-green-800 rounded">
                            <strong>Calculator impact:</strong> Reduces need to withdraw from super, extends super longevity
                        </div>
                        </div>

                        <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎯 When to Use These Features</h5>
                        <div className="text-sm text-purple-700 space-y-2">
                            <div><strong>Use Annuity:</strong></div>
                            <ul className="list-disc list-inside text-purple-600 text-xs ml-4">
                                <li>You're worried about living "too long"</li>
                                <li>Want guaranteed income to cover essentials</li>
                                <li>Age 70+ considering using portion of super</li>
                            </ul>
                            <div><strong>Use Defined Benefit:</strong></div>
                            <ul className="list-disc list-inside text-purple-600 text-xs ml-4">
                                <li>If you have one! Enter the exact amount</li>
                                <li>Helps you calculate how much less to withdraw from super</li>
                            </ul>
                        </div>
                    </div>
                </div>
            )
        },
        {
            title: "Understanding Results & What-If Scenarios",
            content: (
                <div className="space-y-4">
                    <h4 className="font-semibold text-gray-800">Interpreting Your Analysis:</h4>
                    
                    <div className="bg-purple-50 p-4 rounded-lg">
                        <h5 className="font-medium text-purple-800 mb-2">🎲 Monte Carlo Results</h5>
                        <div className="space-y-2 text-sm text-purple-700">
                            <div><strong>Success Rate:</strong></div>
                            <ul className="list-disc list-inside text-purple-600 text-xs ml-4">
                                <li>90%+ = Excellent (high confidence)</li>
                                <li>80-90% = Good (acceptable risk)</li>
                                <li>70-80% = Borderline (consider adjustments)</li>
                                <li>&lt;70% = Risky (likely to fail)</li>
                            </ul>
                            <div><strong>Median/Percentiles:</strong></div>
                            <ul className="list-disc list-inside text-purple-600 text-xs ml-4">
                                <li><strong>10th percentile:</strong> Worst 10% of outcomes</li>
                                <li><strong>50th percentile (median):</strong> Middle outcome</li>
                                <li><strong>90th percentile:</strong> Best 10% of outcomes</li>
                            </ul>
                        </div>
                    </div>

                    <div className="bg-green-50 p-4 rounded-lg">
                        <h5 className="font-medium text-green-800 mb-2">📊 Historical Backtest Results</h5>
                        <p className="text-sm text-green-700 mb-2">Shows every historical retirement period from 1928-2025:</p>
                        <ul className="list-disc list-inside text-green-600 text-sm space-y-1 ml-4">
                            <li><strong>Best case:</strong> Retired during 1982 bull market → huge ending balance</li>
                            <li><strong>Worst case:</strong> Retired during 2000 dot-com crash or 2008 GFC → ran out</li>
                            <li><strong>Median:</strong> Typical historical outcome</li>
                        </ul>
                        <div className="mt-2 p-2 bg-green-200 text-xs text-green-900 rounded">
                            <strong>Key insight:</strong> If your plan failed in past recessions, it may not survive the next one
                        </div>
                    </div>

                        <div className="bg-blue-50 p-4 rounded-lg">
                        <h5 className="font-medium text-blue-800 mb-2">🔧 What-If Scenarios</h5>
                        <p className="text-sm text-blue-700 mb-2">Test how changes affect your plan:</p>
                        <ul className="list-disc list-inside text-blue-600 text-sm space-y-1 ml-4">
                            <li><strong>Change Retirement Period:</strong> Test retiring for 25, 30, or 35 years</li>
                            <li><strong>Change Annual Fees:</strong> See impact of reducing fees by 0.25%, 0.50%, or 1.00%</li>
                            <li><strong>Sequence of Returns Risk:</strong> Test early portfolio drops (-20%, -40%, -60%)</li>
                            <li>Each scenario shows income change and specific impact on your plan</li>
                            </ul>
                        <div className="mt-2 p-2 bg-blue-200 text-xs text-blue-900 rounded">
                            <strong>Pro tip:</strong> Test worst-case scenarios to ensure your plan has safety margin
                        </div>
                    </div>

                    <div className="bg-orange-50 p-4 rounded-lg">
                        <h5 className="font-medium text-orange-800 mb-2">📈 Charts and Visualizations</h5>
                        <div className="text-sm text-orange-700 space-y-1">
                            <div><strong>Balance Over Time:</strong> Shows super balance declining</div>
                            <div><strong>Income Composition:</strong> Where your income comes from each year</div>
                            <div><strong>Distribution Charts:</strong> Range of possible outcomes (Monte Carlo)</div>
                            <div><strong>Historical Overlay:</strong> All past retirement periods on one chart</div>
                        </div>
                        </div>

                    <div className="bg-yellow-50 p-4 rounded-lg">
                        <h5 className="font-medium text-yellow-800 mb-2">💾 Scenario Comparison</h5>
                        <p className="text-sm text-yellow-700 mb-2">Save and compare multiple strategies:</p>
                        <ul className="list-disc list-inside text-yellow-600 text-sm space-y-1 ml-4">
                            <li>Test different withdrawal strategies side-by-side</li>
                            <li>Compare retiring at 65 vs 67 vs 70</li>
                            <li>See impact of different asset allocations</li>
                            <li>Export reports or data for advisers using the available PDF/CSV tools</li>
                            </ul>
                        </div>

                    <div className="bg-indigo-50 p-4 rounded-lg">
                        <h5 className="font-medium text-indigo-800 mb-2">🏦 Division 296 (large balances)</h5>
                        <p className="text-sm text-indigo-700 mb-2">
                            From the {FinancialConfig.DIVISION_296.FIRST_YEAR_FINANCIAL_YEAR} income year, additional tax can apply to
                            earnings attributable to total super above {fmtAud(FinancialConfig.DIVISION_296.THRESHOLD)}
                            (further tier above {fmtAud(FinancialConfig.DIVISION_296.VERY_LARGE_THRESHOLD)}).
                            High-balance projections may show this impact in results — educational modelling only, not an ATO notice.
                        </p>
                        <p className="text-xs text-indigo-600 mt-1">
                            Franking credits still apply only to the Australian shares allocation (see Portfolio Allocation step).
                        </p>
                    </div>

                    <div className="bg-red-50 p-4 rounded-lg">
                        <h5 className="font-medium text-red-800 mb-2">⚠️ What If Your Plan Fails?</h5>
                        <p className="text-sm text-red-700 mb-2">Options to improve success rate:</p>
                        <ul className="list-disc list-inside text-red-600 text-sm space-y-1 ml-4">
                            <li>🕐 <strong>Delay retirement</strong> (highest impact per year)</li>
                            <li>💰 <strong>Reduce spending target</strong> by $5-10k/year</li>
                            <li>📈 <strong>Increase equity allocation</strong> (if you can handle volatility)</li>
                            <li>💵 <strong>Reduce fees</strong> (switch to lower-cost fund)</li>
                            <li>🏠 <strong>Downsize home</strong> to free up capital</li>
                            <li>💼 <strong>Part-time work</strong> in early retirement</li>
                            <li>🎯 <strong>Lower final balance target</strong> (reduce bequest goal)</li>
                            </ul>
                    </div>
                </div>
            )
        }
    ];

    return (
        <div ref={tutorialRef} className="space-y-6">
            {/* Progress Indicator */}
            <div className="flex justify-between items-center mb-6">
                <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-2">
                        <span className="text-sm font-semibold text-gray-700">
                            Step {activeStep + 1} of {tutorialSteps.length}
                        </span>
                        <div className="flex-1 bg-gray-200 rounded-full h-2">
                            <div 
                                className="bg-purple-600 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${((activeStep + 1) / tutorialSteps.length) * 100}%` }}
                        ></div>
                        </div>
                    </div>
                    </div>
                </div>

            {/* Current Step Content */}
            <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                        {tutorialSteps[activeStep].title}
                    </h3>
                <div className="prose max-w-none">
                    {tutorialSteps[activeStep].content}
                </div>
                </div>

            {/* Navigation Buttons */}
                <div className="flex justify-between items-center">
                    <button
                        onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
                        disabled={activeStep === 0}
                    className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    ← Previous
                    </button>
                <div className="flex gap-2">
                    {tutorialSteps.map((_, idx) => (
                            <button
                            key={idx}
                            onClick={() => setActiveStep(idx)}
                            className={`w-2 h-2 rounded-full transition ${
                                idx === activeStep ? 'bg-purple-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                                }`}
                            />
                        ))}
                    </div>
                        <button
                            onClick={() => setActiveStep(Math.min(tutorialSteps.length - 1, activeStep + 1))}
                    disabled={activeStep === tutorialSteps.length - 1}
                    className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                >
                    Next →
                        </button>
            </div>
        </div>
    );
};

export default AdvancedTutorialGuide;



